/**
 * Le statistiche dei lead: fasi, fonti, motivi di perdita, conversione.
 *
 * Stavano dentro la pagina Statistiche Lead. Qui le usano sia la pagina sia i
 * widget della dashboard. I nomi dei risultati sono rimasti quelli della
 * pagina: lo spostamento non doveva cambiare niente di quello che si vede.
 *
 * "adesso" si puo' passare, per provare i conti con un istante fisso.
 */

import {
  LEAD_LOST_REASON_LABELS,
  LEAD_SOURCE_KEYS,
  LEAD_SOURCE_LABELS,
  LEAD_STAGES,
  leadStaleness,
  type Lead,
} from '../../types/leads.types';

const GIORNO_MS = 86_400_000;

export interface VoceConteggio {
  key: string;
  label: string;
  count: number;
}

export interface StatisticheLead {
  total: number;
  byStage: Record<string, number>;
  won: number;
  lost: number;
  /** Ne' vinti ne' persi. */
  active: number;
  /** Vinti sui chiusi, in percentuale intera. */
  conversion: number;
  /** Lead attivi fermi oltre la soglia di ricontatto. */
  recontact: number;
  /** Dal piu' frequente; le fonti senza lead non compaiono. */
  bySource: VoceConteggio[];
  lostByReason: VoceConteggio[];
  /** Giorni medi da creazione a vinto o perso; null se nessuno e' chiuso. */
  avgClose: number | null;
  newThisMonth: number;
  wonThisMonth: number;
}

function contaPerFase(leads: readonly Lead[]): Record<string, number> {
  const perFase: Record<string, number> = {};
  LEAD_STAGES.forEach((st) => { perFase[st.key] = 0; });
  leads.forEach((l) => { perFase[l.stage] = (perFase[l.stage] || 0) + 1; });
  return perFase;
}

function perFonte(leads: readonly Lead[]): VoceConteggio[] {
  return LEAD_SOURCE_KEYS
    .map((fonte) => ({ key: fonte, label: LEAD_SOURCE_LABELS[fonte], count: leads.filter((l) => l.source === fonte).length }))
    .filter((r) => r.count > 0)
    .sort((a, b) => b.count - a.count);
}

/** Il motivo e' salvato come "chiave: dettaglio": conta la chiave. */
function persiPerMotivo(leads: readonly Lead[]): VoceConteggio[] {
  const conteggi: Record<string, number> = {};
  leads.filter((l) => l.stage === 'perso' && l.lost_reason).forEach((l) => {
    const chiave = (l.lost_reason as string).split(':')[0];
    conteggi[chiave] = (conteggi[chiave] || 0) + 1;
  });
  return Object.entries(conteggi)
    .map(([key, count]) => ({ key, label: LEAD_LOST_REASON_LABELS[key] || key, count }))
    .sort((a, b) => b.count - a.count);
}

function giorniMediChiusura(leads: readonly Lead[]): number | null {
  const chiusi = leads.filter((l) => (l.stage === 'vinto' || l.stage === 'perso') && l.stage_changed_at);
  if (chiusi.length === 0) return null;
  const giorni = chiusi.reduce(
    (somma, l) => somma + (new Date(l.stage_changed_at as string).getTime() - new Date(l.created_at).getTime()) / GIORNO_MS,
    0,
  );
  return Math.round(giorni / chiusi.length);
}

export function statisticheLead(
  leads: readonly Lead[],
  giorniRicontatto: number,
  adesso: Date = new Date(),
): StatisticheLead {
  const byStage = contaPerFase(leads);
  const total = leads.length;
  const won = byStage['vinto'] || 0;
  const lost = byStage['perso'] || 0;
  const inizioMese = new Date(adesso.getFullYear(), adesso.getMonth(), 1).getTime();

  return {
    total,
    byStage,
    won,
    lost,
    active: total - won - lost,
    conversion: won + lost > 0 ? Math.round((won / (won + lost)) * 100) : 0,
    recontact: leads.filter((l) => leadStaleness(l, giorniRicontatto, adesso.getTime()).level !== 'fresh').length,
    bySource: perFonte(leads),
    lostByReason: persiPerMotivo(leads),
    avgClose: giorniMediChiusura(leads),
    newThisMonth: leads.filter((l) => new Date(l.created_at).getTime() >= inizioMese).length,
    wonThisMonth: leads.filter(
      (l) => l.stage === 'vinto' && l.stage_changed_at && new Date(l.stage_changed_at).getTime() >= inizioMese,
    ).length,
  };
}
